import { toast } from "shared-components";

import type { ApiInstance, CallRequest } from "@/interfaces/api";
import { useAuthStore } from "@/stores/use-auth-store";
import { captureCurrentRoute } from "@/utils/auth-route";

import { fetchFromBase } from "./fetchFromBase";

const authPaths = ["/login", "/cadastro", "/esqueci-minha-senha", "/redefinir-senha", "/completar-cadastro"];

const isAuthPath = (pathname: string) => authPaths.includes(pathname);

export const api: ApiInstance = {
  defaultError: "Houve um erro inesperado. Contate o suporte para mais informações.",
  _token: null,
  router: null,
  set token(token: string | null) {
    this._token = token;
  },
  get token() {
    return this._token;
  },
  async call(path: string, { method, payload, next }: Omit<CallRequest, "path" | "token">) {
    return fetchFromBase(path, {
      method,
      ...(payload ? { body: JSON.stringify(payload) } : {}),
      next,
    });
  },
  async parse<T>(response: Response): Promise<T> {
    try {
      if (response.status === 204) {
        return null as T;
      }

      const text = await response.text();
      return text ? JSON.parse(text) : (null as T);
    } catch (error) {
      throw new Error("Erro ao processar resposta");
    }
  },
  hasError(response: Response, body: any): body is { message: string } {
    return !response.ok;
  },
  _isHandlingUnauthorizedError: false,
  handleUnauthorizedError() {
    if (typeof window === "undefined" || !this.router || isAuthPath(window.location.pathname)) {
      return;
    }

    if (this._isHandlingUnauthorizedError) {
      return;
    }

    this._isHandlingUnauthorizedError = true;

    const currentRoute = captureCurrentRoute();
    const { cleanSession, setOnAuthRoute } = useAuthStore.getState();

    cleanSession();
    setOnAuthRoute(currentRoute);
    toast.error("Sua sessão expirou. Faça o login novamente.");
    this.router.replace("/login");
  },
  resetUnauthorizedErrorGuard() {
    this._isHandlingUnauthorizedError = false;
  },
};
